import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { formatUnits, parseUnits, isAddress } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useContracts } from '../hooks/useContracts';
import { useNotify } from '../context/NotificationContext';
import { useAdminRole } from '../hooks/useAdminRole';
import { fmtUSD, fmtGRX, truncateAddress, tsToDate, secondsToDHMS } from '../utils/format';
import { ADMIN_API_KEY } from '../config/api';
import { parseError } from '../utils/errors';
import { PLAN_META, rankLabel, displayRank } from '../utils/constants';
import GlassCard from '../components/common/GlassCard';
import StatTile from '../components/common/StatTile';
import NeonButton from '../components/common/NeonButton';
import ConnectPrompt from '../components/common/ConnectPrompt';
import HexLoader from '../components/loader/HexLoader';
import AtomSpinner from '../components/loader/AtomSpinner';

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// bps ↔ % helpers
const bpsToPctStr = (bps) => (Number(bps || 0) / 100).toFixed(2);
const pctStrToBps = (str) => {
  const n = parseFloat(String(str || '0').replace(',', '.'));
  if (isNaN(n)) return 0n;
  return BigInt(Math.round(n * 100));
};

// Per-tab visibility. Function receives the `role` object and returns bool.
// Defaults to "all privileged wallets can see it" if `visible` is absent.
const TABS = [
  { id: 'overview',    label: 'Overview',    icon: '◆' },
  { id: 'setup',       label: 'Setup',       icon: '⚙', visible: (r) => r.isOwner },
  { id: 'payout',      label: 'Payout',      icon: '💰', visible: (r) => r.isOwner || r.isPayoutWallet },
  { id: 'config',      label: 'Runtime',     icon: '⚡', visible: (r) => r.isOwner },
  { id: 'plans',       label: 'Plans',       icon: '⧗' },
  { id: 'levels',      label: 'Levels',      icon: '⇡' },
  { id: 'ranks',       label: 'Ranks',       icon: '▲' },
  { id: 'contributor', label: 'Contributor', icon: '⧖' },
  { id: 'users',       label: 'Users',       icon: '⊕' },
  { id: 'system',      label: 'System',      icon: '⚠', visible: (r) => r.isOwner },
  { id: 'news',        label: 'News',        icon: '★' },
];

const Admin = () => {
  const { account } = useWallet();
  const c = useContracts();
  const notify = useNotify();
  const role = useAdminRole();

  const [tab, setTab] = useState('overview');

  // If the currently-selected tab isn't visible to this role (e.g. admin
  // wallet switched from owner), bounce to Overview so nothing "sticks" hidden.
  useEffect(() => {
    const active = TABS.find((t) => t.id === tab);
    if (active && active.visible && !active.visible(role)) {
      setTab('overview');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.isOwner, role.isAdmin, role.isPayoutWallet, tab]);

  const [busy, setBusy] = useState(false);
  const [txLabel, setTxLabel] = useState('');
  const [snap, setSnap] = useState(null);
  const [snapLoading, setSnapLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!account) return;
      setSnapLoading(true);
      const safe = (p, fallback) => p.catch(() => fallback);
      try {
        const results = await Promise.all([
          safe(c.read.views.getPlanConfig().then((p) => ({
            payoutBal: BigInt(p.payoutWalletBalance || 0),
            payoutAllow: BigInt(p.payoutWalletAllowance || 0),
          })), { payoutBal: 0n, payoutAllow: 0n }),
          safe(c.read.logic.priceOverride(), 0n),
          safe(c.read.logic.minInvestmentUSDT(), 0n),
          safe(c.read.logic.cutoffAnchor(), 0n),
          safe(c.read.logic.cutoffPeriod(), 0n),
          safe(c.read.logic.contributorCycle(), 0n),
          safe(c.read.logic.planDailyROIBps(0), 0n),
          safe(c.read.logic.planDailyROIBps(1), 0n),
          safe(c.read.logic.planDailyROIBps(2), 0n),
          safe(c.read.logic.planDailyROIBps(3), 0n),
          safe(c.read.logic.currentCutoffIndex(), 0n),
          safe(c.read.logic.grxPriceUSDT(), 0n),
          safe(c.read.logic.owner(), null),
          safe(c.read.logic.adminContract(), null),
          safe(c.read.store.paused(), false),
          safe(c.read.store.payoutWallet(), null),
          safe(c.read.store.liquidityWallet(), null),
          safe(c.read.store.adminWallet(), null),
          safe(c.read.store.defaultReferrerId(), 0n),
          safe(c.read.store.defaultReferrerWallet(), null),
          safe(c.read.store.owner(), null),
          safe(c.read.store.admin(), null),
          safe(c.read.store.totalUsers(), 0n),
          safe(c.read.store.totalContributors(), 0n),
          safe(c.read.store.contractGRXBalance(), 0n),
          safe(c.read.logic.CEILING_2X_BPS(), 20000n),
          safe(c.read.logic.CEILING_3X_BPS(), 30000n),
          safe(c.read.logic.CEILING_4X_BPS(), 40000n),
          safe(c.read.logic.DIRECTS_FOR_3X(), 5n),
          safe(c.read.logic.RANK_FOR_4X(), 3n),
          safe(c.read.logic.baseDeductionBps(), 0n),
          safe(c.read.logic.fourXExtraDeductionBps(), 0n),
          safe(c.read.logic.CONTRIBUTOR_MULTIPLIER(), 10n),
          safe(c.read.logic.CONTRIBUTOR_CYCLES(), 20n),
          safe(c.read.logic.CONTRIBUTOR_GRX_BPS(), 7000n),
          safe(c.read.logic.CONTRIBUTOR_USDT_BPS(), 3000n),
          safe(c.read.admin ? c.read.admin.owner() : Promise.resolve(null), null),
          safe(c.read.admin ? c.read.admin.admin() : Promise.resolve(null), null),
          safe(c.read.logic.levelSelfRequirement(), 50n * 10n ** 18n),
          // Storage.authorizedLogic checks — for setup verification
          safe(c.read.store.authorizedLogic(c.addresses.planLogic), false),
          safe(c.hasAdmin
            ? c.read.store.authorizedLogic(c.addresses.planAdmin)
            : Promise.resolve(false), false),
          // Hot-swap logic pointers (both admin + views expose logic())
          safe(c.read.admin ? c.read.admin.logic() : Promise.resolve(null), null),
          safe(c.read.views.logic(), null),
          // Rewards contract wiring — authorized on Storage + set on Logic
          safe(c.hasRewards
            ? c.read.store.authorizedLogic(c.addresses.planRewards)
            : Promise.resolve(false), false),
          safe(c.read.logic.rewardsContract(), null),
        ]);
        if (!mounted) return;
        setSnap({
          payoutBal: results[0].payoutBal, payoutAllow: results[0].payoutAllow,
          priceOverride: results[1], minInv: results[2],
          cutoffAnchor: results[3], cutoffPeriod: results[4], contribCycle: results[5],
          roi: [results[6], results[7], results[8], results[9]],
          curCutoff: results[10], grxPrice: results[11],
          logicOwner: results[12], logicAdminCt: results[13],
          paused: results[14], payoutWallet: results[15],
          liquidityWallet: results[16], adminWallet: results[17],
          defaultRefId: results[18], defaultRefWallet: results[19],
          storageOwner: results[20], storageAdmin: results[21],
          totalUsers: results[22], totalContrib: results[23], contractBal: results[24],
          c2: results[25], c3: results[26], c4: results[27],
          directsFor3X: results[28], rankFor4X: results[29],
          baseDed: results[30], fourXDed: results[31],
          mult: results[32], cycles: results[33], gBps: results[34], uBps: results[35],
          adminOwner: results[36], adminAdmin: results[37],
          levelSelfReq: results[38],
          logicAuthorized: results[39],
          adminAuthorized: results[40],
          planAdminLogic: results[41],
          planViewsLogic: results[42],
          rewardsAuthorized: results[43],
          logicRewardsPtr: results[44],
        });
      } catch (e) {
        console.warn('Admin snapshot failed:', e);
      } finally {
        if (mounted) setSnapLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, refresh]);

  const run = async (label, fn, successMsg) => {
    try {
      setBusy(true); setTxLabel(label);
      const tx = await fn();
      await tx.wait();
      await wait(1500);
      notify.success('Success', successMsg || 'Transaction confirmed.');
      setRefresh((x) => x + 1);
    } catch (e) {
      notify.error(label + ' Failed', parseError(e));
    } finally {
      setBusy(false); setTxLabel('');
    }
  };

  const manualRefresh = () => setRefresh((x) => x + 1);

  if (!account) return <ConnectPrompt />;
  if (role.loading) return (
    <div className="empty-state"><AtomSpinner size="lg" /><div className="empty-title mt-3">Checking Access</div></div>
  );
  if (!role.isPrivileged) return <Navigate to="/" replace />;

  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <div>
          <div className="page-title">Admin Console</div>
          <div className="page-subtitle" style={{ marginLeft: 0 }}>
            Signed in as
            {role.isOwner && <span className="badge b-cyan" style={{ marginLeft: 8 }}>OWNER</span>}
            {role.isAdmin && <span className="badge" style={{ marginLeft: 6 }}>ADMIN</span>}
          </div>
        </div>
        <NeonButton variant="ghost" size="sm" onClick={manualRefresh} loading={snapLoading}>Refresh</NeonButton>
      </div>

      {/* ─────────── UPDATE ROI BAR ─────────── */}
      <AccrueRangeBar c={c} busy={busy} snap={snap} notify={notify} />

      {/* ─────────── TABS (filtered by role) ─────────── */}
      <div className="tabs">
        {TABS.filter((t) => !t.visible || t.visible(role)).map((t) => (
          <button key={t.id} className={'tab' + (tab === t.id ? ' active' : '')} onClick={() => setTab(t.id)}>
            <span>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ─────────── TAB CONTENT (role-gated) ─────────── */}
      {tab === 'overview' && <OverviewTab snap={snap} loading={snapLoading} />}
      {tab === 'setup'    && role.isOwner && <SetupTab snap={snap} loading={snapLoading} c={c} role={role} run={run} busy={busy} />}
      {tab === 'payout'   && (role.isOwner || role.isPayoutWallet) && <PayoutConsoleTab snap={snap} c={c} role={role} run={run} busy={busy} account={account} />}
      {tab === 'config'   && role.isOwner && <ConfigTab snap={snap} c={c} run={run} busy={busy} role={role} />}
      {tab === 'plans'    && <PlansTab snap={snap} c={c} run={run} busy={busy} role={role} />}
      {tab === 'levels'   && <LevelsTab snap={snap} c={c} run={run} busy={busy} role={role} />}
      {tab === 'ranks'    && <RanksTab snap={snap} c={c} run={run} busy={busy} role={role} />}
      {tab === 'contributor' && <ContributorTab snap={snap} c={c} run={run} busy={busy} role={role} />}
      {tab === 'users'    && <UsersTab c={c} run={run} busy={busy} />}
      {tab === 'system'   && role.isOwner && <SystemTab snap={snap} c={c} run={run} busy={busy} role={role} />}
      {tab === 'news'     && (
        <>
          <NewsCard notify={notify} />
          <div className="mt-4" />
          <VideosCard notify={notify} />
        </>
      )}

      {busy && <HexLoader label={txLabel || 'Processing'} sub="Confirm in your wallet." />}
    </div>
  );
};

/* ═══════════════════════ UPDATE ROI (BATCH) ═══════════════════════
   Anyone can call accrueRange(from, to). Chunks of 350 users fit within a
   BSC block gas budget. One SYNC ROI click walks through all chunks
   sequentially — prompts a fresh wallet signature per chunk. */
const CHUNK_SIZE = 350;
const AccrueRangeBar = ({ c, busy, snap, notify }) => {
  const total = Number(snap?.totalUsers || 0n);
  const chunks = Math.max(1, Math.ceil(total / CHUNK_SIZE));
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runAll = async () => {
    if (!c.write.logic) { notify.error('No Signer', 'Connect an owner/admin wallet first.'); return; }
    setRunning(true);
    setProgress(0);
    try {
      for (let i = 0; i < chunks; i++) {
        const from = i * CHUNK_SIZE;
        const to = Math.min(from + CHUNK_SIZE, total);
        try {
          const tx = await c.write.logic.accrueRange(from, to);
          await tx.wait();
          setProgress(i + 1);
        } catch (e) {
          notify.error(`Chunk #${i + 1} failed`, e?.shortMessage || e?.message || 'Sync failed');
          throw e;
        }
      }
      notify.success('SYNC ROI', `All ${total} user${total === 1 ? '' : 's'} processed.`);
    } catch {
      /* progress already updated; error toast shown */
    } finally {
      setRunning(false);
    }
  };

  if (!total) return null;

  return (
    <div className="glass-card update-roi-bar">
      <div className="uroi-head">
        <div>
          <div className="uroi-title">⚡ Update ROI</div>
          <div className="uroi-sub">
            Click after every cutoff · {total} user{total === 1 ? '' : 's'} in {chunks} chunk{chunks === 1 ? '' : 's'}
            {running && ` · Processing ${progress}/${chunks}`}
          </div>
        </div>
        <NeonButton size="lg" onClick={runAll} loading={running || busy} disabled={running || busy}>
          SYNC ROI
        </NeonButton>
      </div>
    </div>
  );
};

/* ═══════════════════════ OVERVIEW ═══════════════════════ */
const OverviewTab = ({ snap, loading }) => {
  if (loading && !snap) return (
    <GlassCard title="Contract Status">
      <div className="empty-state"><AtomSpinner size="lg" /><div className="empty-title mt-3">Loading Status</div></div>
    </GlassCard>
  );
  if (!snap) return <div className="alert a-danger">Could not load contract status.</div>;

  const price = snap.grxPrice ? parseFloat(formatUnits(snap.grxPrice.toString(), 18)) : 0;
  const override = snap.priceOverride ? parseFloat(formatUnits(snap.priceOverride.toString(), 18)) : 0;

  return (
    <>
      <div className="grid grid-4 mb-4">
        <StatTile label="Contract State" value={snap.paused ? 'PAUSED' : 'LIVE'} color={snap.paused ? undefined : 'neon'} sub={snap.paused ? '⚠ Halted' : 'Normal operation'} />
        <StatTile label="Payout Wallet Bal" value={fmtGRX(snap.payoutBal)} color="cyan" sub={'Allowance: ' + fmtGRX(snap.payoutAllow)} />
        <StatTile label="Total Users" value={String(snap.totalUsers)} sub={'Contributors: ' + String(snap.totalContrib)} color="neon" />
        <StatTile label="Contract GRX" value={fmtGRX(snap.contractBal)} sub="Flexible principals only" color="cyan" />
      </div>

      <GlassCard title="Runtime Snapshot">
        <div className="grid grid-2">
          <div>
            <div className="mono uppercase text-dim" style={{ fontSize: 11, letterSpacing: '0.14em', marginBottom: 8 }}>Live</div>
            <div className="data-row"><span className="label">Paused</span><span className="value">{snap.paused ? <span className="badge b-danger">YES</span> : <span className="badge">NO</span>}</span></div>
            <div className="data-row"><span className="label">Price Source</span><span className="value">{override > 0 ? 'OVERRIDE ($' + override.toFixed(8) + ')' : 'PancakeSwap'}</span></div>
            <div className="data-row"><span className="label">Live GRX Price</span><span className="value neon">${price.toFixed(8)}</span></div>
            <div className="data-row"><span className="label">Min Investment</span><span className="value">{fmtUSD(snap.minInv)}</span></div>
            <div className="data-row"><span className="label">Base Deduction</span><span className="value">{bpsToPctStr(snap.baseDed)}%</span></div>
            <div className="data-row"><span className="label">4X Extra Deduction</span><span className="value">{bpsToPctStr(snap.fourXDed)}%</span></div>
          </div>
          <div>
            <div className="mono uppercase text-dim" style={{ fontSize: 11, letterSpacing: '0.14em', marginBottom: 8 }}>Timing</div>
            <div className="data-row"><span className="label">Current Cutoff #</span><span className="value mono">{String(snap.curCutoff)}</span></div>
            <div className="data-row"><span className="label">Cutoff Anchor</span><span className="value mono">{tsToDate(snap.cutoffAnchor)}</span></div>
            <div className="data-row"><span className="label">Cutoff Period</span><span className="value mono">{secondsToDHMS(Number(snap.cutoffPeriod))}</span></div>
            <div className="data-row"><span className="label">Contributor Cycle</span><span className="value mono">{secondsToDHMS(Number(snap.contribCycle))}</span></div>
            <div className="data-row"><span className="label">Total Cycles</span><span className="value mono">{String(snap.cycles)}</span></div>
          </div>
        </div>

        <div className="grid grid-2 mt-4">
          <div>
            <div className="mono uppercase text-dim" style={{ fontSize: 11, letterSpacing: '0.14em', marginBottom: 8 }}>Ceiling Ladder</div>
            <div className="data-row"><span className="label">2X Base</span><span className="value">{bpsToPctStr(snap.c2)}%</span></div>
            <div className="data-row"><span className="label">3X @ {String(snap.directsFor3X)} directs</span><span className="value">{bpsToPctStr(snap.c3)}%</span></div>
            <div className="data-row"><span className="label">4X @ {String(snap.directsFor3X)} directs + R{String(snap.rankFor4X)}</span><span className="value">{bpsToPctStr(snap.c4)}%</span></div>
          </div>
          <div>
            <div className="mono uppercase text-dim" style={{ fontSize: 11, letterSpacing: '0.14em', marginBottom: 8 }}>Contributor</div>
            <div className="data-row"><span className="label">Multiplier</span><span className="value">{String(snap.mult)}X</span></div>
            <div className="data-row"><span className="label">GRX Portion</span><span className="value">{bpsToPctStr(snap.gBps)}%</span></div>
            <div className="data-row"><span className="label">USDT Portion</span><span className="value">{bpsToPctStr(snap.uBps)}%</span></div>
            <div className="data-row"><span className="label">Payout Wallet</span><span className="value mono">{truncateAddress(snap.payoutWallet)}</span></div>
            <div className="data-row"><span className="label">Payout Bal / Allow</span><span className="value">{fmtGRX(snap.payoutBal)} / {fmtGRX(snap.payoutAllow)}</span></div>
          </div>
        </div>
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ SETUP CHECK ═══════════════════════ */
const SetupTab = ({ snap, loading, c, role, run, busy }) => {
  if (loading || !snap) return (
    <GlassCard title="Contract Setup"><div className="empty-state"><AtomSpinner size="lg" /></div></GlassCard>
  );

  const nonZero = (a) => a && a !== '0x0000000000000000000000000000000000000000';
  const eq = (a, b) => a && b && String(a).toLowerCase() === String(b).toLowerCase();

  const checks = [
    {
      title: 'Storage contract configured',
      pass: nonZero(c.addresses.storage),
      desc: c.addresses.storage,
      fix: 'Fill the storage address in src/config/contracts.js',
    },
    {
      title: 'PlanLogic contract configured',
      pass: nonZero(c.addresses.planLogic),
      desc: c.addresses.planLogic,
      fix: 'Fill the planLogic address in src/config/contracts.js',
    },
    {
      title: 'PlanAdmin contract configured',
      pass: nonZero(c.addresses.planAdmin),
      desc: c.addresses.planAdmin || '(not set)',
      fix: 'Fill the planAdmin address in src/config/contracts.js',
    },
    {
      title: 'PlanViews contract configured',
      pass: nonZero(c.addresses.planViews),
      desc: c.addresses.planViews,
      fix: 'Fill the planViews address in src/config/contracts.js',
    },
    {
      title: 'Storage → authorized PlanLogic',
      pass: !!snap.logicAuthorized,
      desc: 'Storage.authorizedLogic(' + truncateAddress(c.addresses.planLogic) + ') = ' + (snap.logicAuthorized ? 'true' : 'false'),
      fix: 'Call Storage.setAuthorizedLogic(planLogic, true)',
      action: role.isOwner ? {
        label: 'Authorize',
        fn: () => c.write.store.setAuthorizedLogic(c.addresses.planLogic, true),
      } : null,
    },
    {
      title: 'Storage → authorized PlanAdmin',
      pass: !!snap.adminAuthorized,
      desc: c.hasAdmin
        ? 'Storage.authorizedLogic(' + truncateAddress(c.addresses.planAdmin) + ') = ' + (snap.adminAuthorized ? 'true' : 'false')
        : 'PlanAdmin address not configured',
      fix: 'Call Storage.setAuthorizedLogic(planAdmin, true)',
      action: role.isOwner && c.hasAdmin ? {
        label: 'Authorize',
        fn: () => c.write.store.setAuthorizedLogic(c.addresses.planAdmin, true),
      } : null,
    },
    {
      title: 'PlanLogic → adminContract matches PlanAdmin',
      pass: c.hasAdmin && eq(snap.logicAdminCt, c.addresses.planAdmin),
      desc: 'PlanLogic.adminContract() = ' + (snap.logicAdminCt || '(not set)'),
      fix: 'Call PlanLogic.setAdminContract(planAdmin)',
      action: role.isOwner && c.hasAdmin ? {
        label: 'Set',
        fn: () => c.write.logic.setAdminContract(c.addresses.planAdmin),
      } : null,
    },
    {
      title: 'PlanAdmin.logic() matches deployed PlanLogic',
      pass: c.hasAdmin && eq(snap.planAdminLogic, c.addresses.planLogic),
      desc: 'PlanAdmin.logic() = ' + (snap.planAdminLogic || '(not set)'),
      fix: 'Call PlanAdmin.setLogic(planLogic)',
      action: role.isOwner && c.hasAdmin ? {
        label: 'Set',
        fn: () => c.write.admin.setLogic(c.addresses.planLogic),
      } : null,
    },
    {
      title: 'PlanViews.logic() matches deployed PlanLogic',
      pass: eq(snap.planViewsLogic, c.addresses.planLogic),
      desc: 'PlanViews.logic() = ' + (snap.planViewsLogic || '(not set)'),
      fix: 'Call PlanViews.setLogic(planLogic)',
      action: role.isOwner ? {
        label: 'Set',
        fn: () => c.write.views.setLogic(c.addresses.planLogic),
      } : null,
    },
    {
      title: 'PlanRewards is authorized on Storage',
      pass: c.hasRewards && !!snap.rewardsAuthorized,
      desc: c.hasRewards
        ? 'Storage.authorizedLogic(' + truncateAddress(c.addresses.planRewards) + ') = ' + (snap.rewardsAuthorized ? 'true' : 'false')
        : 'planRewards address not set in contracts.js',
      fix: 'Call Storage.setAuthorizedLogic(planRewards, true)',
      action: role.isOwner && c.hasRewards ? {
        label: 'Authorize',
        fn: () => c.write.store.setAuthorizedLogic(c.addresses.planRewards, true),
      } : null,
    },
    {
      title: 'PlanLogic.rewardsContract() matches deployed PlanRewards',
      pass: c.hasRewards && eq(snap.logicRewardsPtr, c.addresses.planRewards),
      desc: 'PlanLogic.rewardsContract() = ' + (snap.logicRewardsPtr || '(not set)'),
      fix: 'Call PlanLogic.setRewardsContract(planRewards)',
      action: role.isOwner && c.hasRewards ? {
        label: 'Set',
        fn: () => c.write.logic.setRewardsContract(c.addresses.planRewards),
      } : null,
    },
  ];

  const passed = checks.filter((k) => k.pass).length;
  const allOk = passed === checks.length;

  return (
    <>
      <div className="grid grid-3 mb-4">
        <StatTile
          label="Setup Progress"
          value={passed + ' / ' + checks.length}
          sub={allOk ? 'All good' : 'Action required'}
          color={allOk ? 'neon' : undefined}
        />
        <StatTile label="Storage" value={truncateAddress(c.addresses.storage)} sub="Data layer" color="cyan" />
        <StatTile label="PlanLogic" value={truncateAddress(c.addresses.planLogic)} sub="Business logic" color="cyan" />
      </div>

      <GlassCard title="Contract Wiring Verification">
        {checks.map((k, i) => (
          <div key={i} className={'setup-row ' + (k.pass ? 'ok' : 'bad')}>
            <div className={'setup-icon ' + (k.pass ? 'ok' : 'bad')}>{k.pass ? '✓' : '✕'}</div>
            <div className="setup-body">
              <div className="setup-title">{k.title}</div>
              <div className="setup-desc">{k.desc}</div>
              {!k.pass && <div className="setup-desc" style={{ color: 'var(--grx-warn)', marginTop: 4 }}>Fix: {k.fix}</div>}
            </div>
            {!k.pass && k.action && (
              <NeonButton size="sm" onClick={() => run(k.action.label, k.action.fn, k.title + ' fixed.')} loading={busy}>
                {k.action.label}
              </NeonButton>
            )}
          </div>
        ))}
      </GlassCard>

      <div className="mt-4" />
      <GlassCard title="Role Addresses">
        <div className="data-row"><span className="label">Logic Owner</span><span className="value mono">{snap.logicOwner || '—'}</span></div>
        <div className="data-row"><span className="label">Logic → AdminContract</span><span className="value mono">{snap.logicAdminCt || '—'}</span></div>
        <div className="data-row"><span className="label">Storage Owner</span><span className="value mono">{snap.storageOwner || '—'}</span></div>
        <div className="data-row"><span className="label">Storage Admin</span><span className="value mono">{snap.storageAdmin || '—'}</span></div>
        <div className="data-row"><span className="label">PlanAdmin Owner</span><span className="value mono">{snap.adminOwner || '—'}</span></div>
        <div className="data-row"><span className="label">PlanAdmin Admin</span><span className="value mono">{snap.adminAdmin || '—'}</span></div>
        <div className="data-row"><span className="label">Liquidity Wallet</span><span className="value mono">{snap.liquidityWallet || '—'}</span></div>
        <div className="data-row"><span className="label">Admin Wallet</span><span className="value mono">{snap.adminWallet || '—'}</span></div>
        <div className="data-row"><span className="label">Default Referrer</span><span className="value mono">#{String(snap.defaultRefId)} · {truncateAddress(snap.defaultRefWallet)}</span></div>
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ PAYOUT CONSOLE ═══════════════════════ */
// One place for the payout wallet to approve GRX (unlimited) to Storage.
// Owner can also see this to verify status. If the currently connected wallet
// is NOT the configured payoutWallet, we show a clear callout telling them
// which wallet to switch to.
const MAX_UINT = (2n ** 256n) - 1n;
const APPROVE_HIGH_THRESHOLD = 10n ** 18n * 10_000_000_000n; // 10 billion GRX = "unlimited enough"

const PayoutConsoleTab = ({ snap, c, role, run, busy, account }) => {
  const payoutAddr = snap?.payoutWallet ? String(snap.payoutWallet) : null;
  const bal   = snap?.payoutBal   ? BigInt(snap.payoutBal)   : 0n;
  const allow = snap?.payoutAllow ? BigInt(snap.payoutAllow) : 0n;
  const isMe  = role.isPayoutWallet;
  const alreadyApproved = allow >= APPROVE_HIGH_THRESHOLD;

  const approveMax = () =>
    run('Approving Unlimited GRX', () => c.write.grx.approve(c.addresses.storage, MAX_UINT), 'Storage can now pull GRX for payouts.');
  const revoke = () =>
    run('Revoking GRX Allowance', () => c.write.grx.approve(c.addresses.storage, 0n), 'Allowance revoked. Storage can no longer pull GRX.');

  return (
    <>
      <div className="grid grid-4 mb-4">
        <StatTile
          label="Payout Wallet"
          value={payoutAddr ? truncateAddress(payoutAddr) : '—'}
          sub={isMe ? 'You are the payout wallet ✓' : 'Different wallet configured'}
          color={isMe ? 'neon' : undefined}
        />
        <StatTile label="Balance" value={fmtGRX(bal)} color="cyan" />
        <StatTile
          label="Allowance"
          value={allow === 0n ? 'NONE' : alreadyApproved ? 'UNLIMITED' : fmtGRX(allow)}
          color={alreadyApproved ? 'neon' : (allow === 0n ? undefined : 'cyan')}
        />
        <StatTile label="Storage Contract" value={truncateAddress(c.addresses.storage)} sub="Spender that pulls GRX" />
      </div>

      <GlassCard title="Payout Wallet Approval">
        {!payoutAddr && (
          <div className="alert a-danger">
            No payout wallet configured yet. Owner must set it in the <strong>System</strong> tab first.
          </div>
        )}

        {payoutAddr && !isMe && (
          <>
            <div className="alert a-warn">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Wrong wallet connected</div>
              <div style={{ fontSize: 13 }}>
                To approve GRX, you must connect the configured payout wallet in your browser wallet.
              </div>
              <div className="mono mt-2" style={{ fontSize: 12, color: 'var(--grx-text-mute)' }}>
                Payout wallet:
              </div>
              <div className="mono" style={{ fontSize: 13, color: 'var(--grx-neon)', wordBreak: 'break-all' }}>
                {payoutAddr}
              </div>
              <div className="mono mt-2" style={{ fontSize: 12, color: 'var(--grx-text-mute)' }}>
                You are connected as: {account}
              </div>
            </div>
            <div className="mt-3 data-row">
              <span className="label">Current GRX Allowance</span>
              <span className="value">
                {alreadyApproved
                  ? <span className="badge">UNLIMITED · READY</span>
                  : allow === 0n
                    ? <span className="badge b-danger">NOT APPROVED</span>
                    : <span className="badge b-warn">Partial: {fmtGRX(allow)}</span>}
              </span>
            </div>
          </>
        )}

        {payoutAddr && isMe && (
          <>
            <div className="alert a-info">
              You're connected as the payout wallet. Approve <strong>unlimited GRX</strong> to the storage contract so it can push GRX to users on every reward claim and contributor payout. This is a one-time gasless-forever action (no allowance decrement).
            </div>

            <div className="data-row">
              <span className="label">Your GRX Balance</span>
              <span className="value cyan">{fmtGRX(bal)}</span>
            </div>
            <div className="data-row">
              <span className="label">Current Allowance</span>
              <span className="value">
                {alreadyApproved
                  ? <span className="badge">UNLIMITED · READY</span>
                  : allow === 0n
                    ? <span className="badge b-danger">NOT APPROVED</span>
                    : <span className="badge b-warn">Partial: {fmtGRX(allow)}</span>}
              </span>
            </div>
            <div className="data-row">
              <span className="label">Spender</span>
              <span className="value mono">{c.addresses.storage}</span>
            </div>

            <div className="mt-3" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <NeonButton
                size="lg"
                onClick={approveMax}
                loading={busy}
                disabled={alreadyApproved}
              >
                {alreadyApproved ? '✓ Already Unlimited' : 'Approve Unlimited GRX'}
              </NeonButton>
              <NeonButton
                size="lg"
                variant="danger"
                onClick={revoke}
                loading={busy}
                disabled={allow === 0n}
              >
                Revoke Approval
              </NeonButton>
            </div>

            {alreadyApproved && (
              <div className="alert a-info mt-3" style={{ background: 'rgba(0, 255, 157, 0.06)' }}>
                ✓ Payouts are fully enabled. Users will receive GRX seamlessly on claim.
              </div>
            )}

            {bal === 0n && (
              <div className="alert a-warn mt-3">
                ⚠ Your GRX balance is 0. Deposit GRX into this wallet before users try to claim, or claims will fail with a transfer error.
              </div>
            )}
          </>
        )}
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ HELPERS ═══════════════════════ */
const requireAdminWrite = (c) => {
  if (!c.write.admin) throw new Error('GRXPlanAdmin write not available. Check contracts.js address and setup tab.');
  return c.write.admin;
};

/* ═══════════════════════ CONFIG / RUNTIME ═══════════════════════ */
const ConfigTab = ({ snap, c, run, busy, role }) => {
  const [anchor, setAnchor] = useState('');
  const [period, setPeriod] = useState('');
  const [cCycle, setCCycle] = useState('');
  const [priceVal, setPriceVal] = useState('');

  const current = snap?.priceOverride ? parseFloat(formatUnits(snap.priceOverride.toString(), 18)) : 0;

  if (!role.isOwner && !role.isAdmin) return null;

  return (
    <>
      <div className="grid grid-2">
        <GlassCard title="GRX Price Override">
          <div className="data-row">
            <span className="label">Current</span>
            <span className="value">{current > 0 ? '$' + current.toFixed(8) : <span className="badge b-dim">POOL</span>}</span>
          </div>
          <div className="alert a-info mt-2">Set 0 to use PancakeSwap pool.</div>
          <div className="input-label mt-3"><span>New Price (USDT per GRX)</span><span>0 disables</span></div>
          <input type="number" step="0.000001" value={priceVal} onChange={(e) => setPriceVal(e.target.value)} />
          <div className="mt-3">
            <NeonButton block disabled={!c.hasAdmin} loading={busy}
              onClick={() => run('Set Price Override', () => requireAdminWrite(c).setPriceOverride(parseUnits(priceVal || '0', 18)), 'Price updated.')}>
              Update Price
            </NeonButton>
          </div>
        </GlassCard>
      </div>

      <div className="mt-4" />
      <GlassCard title="Timing">
        <div className="alert a-info">
          Test preset: cutoffs every 60s, contributor releases every 120s. Prod defaults: anchor=1704076200, period=43200 (12h), cycle=7776000 (90d).
        </div>
        <div className="grid grid-3 mt-3">
          <div>
            <div className="input-label"><span>Anchor (unix)</span></div>
            <input type="number" value={anchor} onChange={(e) => setAnchor(e.target.value)} />
            <div className="mt-2">
              <NeonButton block size="sm" disabled={!c.hasAdmin || !anchor} loading={busy}
                onClick={() => run('Set Anchor', () => requireAdminWrite(c).setCutoffAnchor(BigInt(anchor)), 'Anchor updated.')}>Set Anchor</NeonButton>
            </div>
          </div>
          <div>
            <div className="input-label"><span>Cutoff Period (s)</span></div>
            <input type="number" value={period} onChange={(e) => setPeriod(e.target.value)} />
            <div className="mt-2">
              <NeonButton block size="sm" disabled={!c.hasAdmin || !period} loading={busy}
                onClick={() => run('Set Period', () => requireAdminWrite(c).setCutoffPeriod(BigInt(period)), 'Period updated.')}>Set Period</NeonButton>
            </div>
          </div>
          <div>
            <div className="input-label"><span>Contributor Cycle (s)</span></div>
            <input type="number" value={cCycle} onChange={(e) => setCCycle(e.target.value)} />
            <div className="mt-2">
              <NeonButton block size="sm" disabled={!c.hasAdmin || !cCycle} loading={busy}
                onClick={() => run('Set Cycle', () => requireAdminWrite(c).setContributorCycle(BigInt(cCycle)), 'Cycle updated.')}>Set Cycle</NeonButton>
            </div>
          </div>
        </div>
        <div className="mt-3" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <NeonButton variant="ghost" onClick={() => {
            const now = Math.floor(Date.now() / 1000);
            setAnchor(String(now)); setPeriod('60'); setCCycle('120');
          }}>Fill Test Preset</NeonButton>
          <NeonButton variant="ghost" onClick={() => { setAnchor('1704076200'); setPeriod('43200'); setCCycle('7776000'); }}>Fill Prod Defaults</NeonButton>
        </div>
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ PLANS / ROI / DEDUCTIONS / MIN ═══════════════════════ */
const PlansTab = ({ snap, c, run, busy }) => {
  const [pt, setPt] = useState(0);
  const [pctInput, setPctInput] = useState('');
  const [minInvest, setMinInvest] = useState('');
  const [minClaim, setMinClaim] = useState('');
  const [minClaimNow, setMinClaimNow] = useState(0n);
  const [baseDed, setBaseDed] = useState('');
  const [fourDed, setFourDed] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const v = await c.read.logic.minClaimUSDT().catch(() => 0n);
        if (mounted) setMinClaimNow(BigInt(v));
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="grid grid-2">
        <GlassCard title="Plan Daily ROI">
          {snap?.roi?.map((r, i) => (
            <div key={i} className="data-row">
              <span className="label">{PLAN_META[i].name}</span>
              <span className="value neon">{bpsToPctStr(r)}%</span>
            </div>
          ))}
          <div className="input-label mt-3"><span>Plan</span><span /></div>
          <select value={pt} onChange={(e) => setPt(Number(e.target.value))}>
            {PLAN_META.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="input-label mt-3"><span>New Daily ROI (%)</span><span>Max 10%</span></div>
          <input type="number" step="0.01" placeholder="e.g. 0.50" value={pctInput} onChange={(e) => setPctInput(e.target.value)} />
          <div className="mt-3">
            <NeonButton block disabled={!c.hasAdmin} loading={busy}
              onClick={() => run('Set Plan ROI', () => requireAdminWrite(c).setPlanDailyROIBps(pt, pctStrToBps(pctInput)), 'ROI updated.')}>
              Update ROI
            </NeonButton>
          </div>
        </GlassCard>

        <GlassCard title="Minimum Investment">
          <div className="data-row"><span className="label">Current</span><span className="value">{fmtUSD(snap?.minInv)}</span></div>
          <div className="input-label mt-3"><span>New Minimum (USDT)</span></div>
          <input type="number" placeholder="50" value={minInvest} onChange={(e) => setMinInvest(e.target.value)} />
          <div className="mt-3">
            <NeonButton block disabled={!c.hasAdmin} loading={busy}
              onClick={() => run('Set Min Investment', () => requireAdminWrite(c).setMinInvestment(parseUnits(minInvest || '0', 18)), 'Minimum updated.')}>
              Update Minimum
            </NeonButton>
          </div>
        </GlassCard>

        <GlassCard title="Minimum Claim">
          <div className="data-row"><span className="label">Current</span><span className="value">{fmtUSD(minClaimNow)}</span></div>
          <div className="input-label mt-3"><span>New Min Claim (USDT)</span><span>0 = no minimum</span></div>
          <input type="number" placeholder="0" value={minClaim} onChange={(e) => setMinClaim(e.target.value)} />
          <div className="mt-3">
            <NeonButton block disabled={!c.hasAdmin} loading={busy}
              onClick={() => run('Set Min Claim', () => requireAdminWrite(c).setMinClaimUSDT(parseUnits(minClaim || '0', 18)), 'Min claim updated.')}>
              Update Min Claim
            </NeonButton>
          </div>
        </GlassCard>
      </div>

      <div className="mt-4" />
      <GlassCard title="Reward Claim Deductions">
        <div className="grid grid-2">
          <div>
            <div className="data-row">
              <span className="label">Base Deduction</span>
              <span className="value neon">{bpsToPctStr(snap?.baseDed)}%</span>
            </div>
            <div className="input-label mt-3"><span>New Base (%)</span><span>Max 50%</span></div>
            <input type="number" step="0.01" placeholder="0.00" value={baseDed} onChange={(e) => setBaseDed(e.target.value)} />
            <div className="mt-2">
              <NeonButton block disabled={!c.hasAdmin} loading={busy}
                onClick={() => run('Set Base Deduction', () => requireAdminWrite(c).setBaseDeductionBps(pctStrToBps(baseDed)), 'Base updated.')}>
                Update Base
              </NeonButton>
            </div>
          </div>
          <div>
            <div className="data-row">
              <span className="label">4X Extra Deduction</span>
              <span className="value neon">{bpsToPctStr(snap?.fourXDed)}%</span>
            </div>
            <div className="input-label mt-3"><span>New 4X Extra (%)</span><span>Max 50%</span></div>
            <input type="number" step="0.01" placeholder="0.00" value={fourDed} onChange={(e) => setFourDed(e.target.value)} />
            <div className="mt-2">
              <NeonButton block disabled={!c.hasAdmin} loading={busy}
                onClick={() => run('Set 4X Extra', () => requireAdminWrite(c).setFourXExtraDeductionBps(pctStrToBps(fourDed)), '4X extra updated.')}>
                Update 4X Extra
              </NeonButton>
            </div>
          </div>
        </div>
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ LEVELS ═══════════════════════ */
const LevelsTab = ({ snap, c, run, busy }) => {
  const [rows, setRows] = useState(null); // [{pct, dir}]
  const [selfReq, setSelfReq] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const promises = [];
        for (let i = 0; i < 30; i++) {
          promises.push(c.read.logic.levelPercentages(i).catch(() => 0n));
          promises.push(c.read.logic.levelDirectRequirement(i).catch(() => 0n));
        }
        const out = await Promise.all(promises);
        if (!mounted) return;
        const arr = [];
        for (let i = 0; i < 30; i++) {
          arr.push({ pct: bpsToPctStr(out[i * 2]), dir: String(out[i * 2 + 1]) });
        }
        setRows(arr);
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (i, field, val) => {
    setDirty(true);
    setRows((prev) => prev.map((r, j) => j === i ? { ...r, [field]: val } : r));
  };

  const savePct = (i) => run('Set L' + (i + 1) + ' %',
    () => requireAdminWrite(c).setLevelPercentage(i, pctStrToBps(rows[i].pct)),
    'L' + (i + 1) + ' percentage updated.');
  const saveDir = (i) => run('Set L' + (i + 1) + ' Directs',
    () => requireAdminWrite(c).setLevelDirectRequirement(i, BigInt(rows[i].dir || 0)),
    'L' + (i + 1) + ' directs requirement updated.');

  return (
    <>
      <GlassCard title="Level Self-Investment Requirement">
        <div className="data-row"><span className="label">Current</span><span className="value">{fmtUSD(snap?.levelSelfReq)}</span></div>
        <div className="input-label mt-3"><span>New Requirement (USDT)</span></div>
        <input type="number" placeholder="50" value={selfReq} onChange={(e) => setSelfReq(e.target.value)} />
        <div className="mt-3">
          <NeonButton disabled={!c.hasAdmin} loading={busy}
            onClick={() => run('Set Level Self Req', () => requireAdminWrite(c).setLevelSelfRequirement(parseUnits(selfReq || '0', 18)), 'Self-req updated.')}>
            Update Self Requirement
          </NeonButton>
        </div>
      </GlassCard>

      <div className="mt-4" />
      <GlassCard title="30-Level Income Configuration">
        {!rows ? (
          <div className="empty-state"><AtomSpinner size="lg" /></div>
        ) : (
          <div className="table-wrap">
            <table className="grx-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Percentage (%)</th>
                  <th>Directs Required</th>
                  <th>Save</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>L{i + 1}</td>
                    <td>
                      <input type="number" step="0.01" value={r.pct}
                        onChange={(e) => updateRow(i, 'pct', e.target.value)}
                        style={{ padding: '6px 8px', maxWidth: 120 }} />
                    </td>
                    <td>
                      <input type="number" value={r.dir}
                        onChange={(e) => updateRow(i, 'dir', e.target.value)}
                        style={{ padding: '6px 8px', maxWidth: 100 }} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => savePct(i)}>%</NeonButton>
                        <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => saveDir(i)}>Dir</NeonButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {dirty && (
          <div className="alert a-info mt-3">
            Save each row individually. Percent and Directs are separate transactions.
          </div>
        )}
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ RANKS ═══════════════════════ */
const RanksTab = ({ c, run, busy }) => {
  const [rows, setRows] = useState(null); // [{main, other, self, pct, override}]

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const promises = [];
        for (let r = 1; r <= 9; r++) {
          promises.push(c.read.logic.rankMainLeg(r).catch(() => 0n));
          promises.push(c.read.logic.rankOtherLegs(r).catch(() => 0n));
          promises.push(c.read.logic.rankSelfRequirement(r).catch(() => 0n));
          promises.push(c.read.logic.rankPercentageBps(r).catch(() => 0n));
          promises.push(c.read.logic.sameRankOverrideBps(r).catch(() => 0n));
        }
        const out = await Promise.all(promises);
        if (!mounted) return;
        const arr = [];
        for (let r = 0; r < 9; r++) {
          arr.push({
            main:     parseFloat(formatUnits(out[r * 5].toString(),     18)).toString(),
            other:    parseFloat(formatUnits(out[r * 5 + 1].toString(), 18)).toString(),
            self:     parseFloat(formatUnits(out[r * 5 + 2].toString(), 18)).toString(),
            pct:      bpsToPctStr(out[r * 5 + 3]),
            override: bpsToPctStr(out[r * 5 + 4]),
          });
        }
        setRows(arr);
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (i, k, v) => setRows((p) => p.map((r, j) => j === i ? { ...r, [k]: v } : r));

  const doSave = (rankIdx, field) => {
    const rank = rankIdx + 1;
    const row = rows[rankIdx];
    let fn, label;
    if (field === 'main')       { fn = () => requireAdminWrite(c).setRankMainLeg(rank, parseUnits(row.main || '0', 18));       label = 'Set R' + rank + ' Main'; }
    else if (field === 'other') { fn = () => requireAdminWrite(c).setRankOtherLegs(rank, parseUnits(row.other || '0', 18));    label = 'Set R' + rank + ' Others'; }
    else if (field === 'self')  { fn = () => requireAdminWrite(c).setRankSelfRequirement(rank, parseUnits(row.self || '0', 18)); label = 'Set R' + rank + ' Self'; }
    else if (field === 'pct')   { fn = () => requireAdminWrite(c).setRankPercentageBps(rank, pctStrToBps(row.pct));            label = 'Set R' + rank + ' %'; }
    else                        { fn = () => requireAdminWrite(c).setSameRankOverrideBps(rank, pctStrToBps(row.override));     label = 'Set R' + rank + ' Override'; }
    run(label, fn, label + ' updated.');
  };

  return (
    <GlassCard title="9-Rank Configuration">
      {!rows ? <div className="empty-state"><AtomSpinner size="lg" /></div> : (
        <div className="table-wrap">
          <table className="grx-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Main Leg (USDT)</th>
                <th>Other Legs (USDT)</th>
                <th>Self Invest (USDT)</th>
                <th>Rank %</th>
                <th>Same-Rank Override %</th>
                <th>Save</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>R{displayRank(i + 1)} · <span className="text-dim">{rankLabel(i + 1)}</span></td>
                  <td><input type="number" value={r.main}     onChange={(e) => upd(i, 'main',     e.target.value)} style={{ padding: '6px 8px', maxWidth: 140 }} /></td>
                  <td><input type="number" value={r.other}    onChange={(e) => upd(i, 'other',    e.target.value)} style={{ padding: '6px 8px', maxWidth: 140 }} /></td>
                  <td><input type="number" value={r.self}     onChange={(e) => upd(i, 'self',     e.target.value)} style={{ padding: '6px 8px', maxWidth: 120 }} /></td>
                  <td><input type="number" step="0.01" value={r.pct}      onChange={(e) => upd(i, 'pct',      e.target.value)} style={{ padding: '6px 8px', maxWidth: 90 }} /></td>
                  <td><input type="number" step="0.01" value={r.override} onChange={(e) => upd(i, 'override', e.target.value)} style={{ padding: '6px 8px', maxWidth: 90 }} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => doSave(i, 'main')}>M</NeonButton>
                      <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => doSave(i, 'other')}>O</NeonButton>
                      <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => doSave(i, 'self')}>S</NeonButton>
                      <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => doSave(i, 'pct')}>%</NeonButton>
                      <NeonButton size="sm" variant="ghost" disabled={!c.hasAdmin} loading={busy} onClick={() => doSave(i, 'override')}>Ov</NeonButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
};

/* ═══════════════════════ CONTRIBUTOR ═══════════════════════ */
const ContributorTab = ({ snap, c, run, busy }) => {
  const [sponsors, setSponsors] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const out = await Promise.all(
          [0, 1, 2, 3, 4].map((i) => c.read.logic.contributorSponsorBps(i).catch(() => 0n))
        );
        if (!mounted) return;
        setSponsors(out.map((b) => bpsToPctStr(b)));
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (i, v) => setSponsors((p) => p.map((x, j) => j === i ? v : x));

  return (
    <>
      <GlassCard title="Contributor Constants (read-only)">
        <div className="grid grid-2">
          <div className="data-row"><span className="label">Multiplier</span><span className="value">{String(snap?.mult || 10)}X</span></div>
          <div className="data-row"><span className="label">Cycles</span><span className="value">{String(snap?.cycles || 20)}</span></div>
          <div className="data-row"><span className="label">GRX Portion</span><span className="value">{bpsToPctStr(snap?.gBps || 7000n)}%</span></div>
          <div className="data-row"><span className="label">USDT Portion</span><span className="value">{bpsToPctStr(snap?.uBps || 3000n)}%</span></div>
          <div className="data-row"><span className="label">Cycle Period</span><span className="value">{secondsToDHMS(Number(snap?.contribCycle || 0))}</span></div>
        </div>
      </GlassCard>

      <div className="mt-4" />
      <GlassCard title="Contributor Sponsor Income (Levels 1-5)">
        {!sponsors ? <div className="empty-state"><AtomSpinner size="lg" /></div> : (
          <div className="table-wrap">
            <table className="grx-table">
              <thead>
                <tr><th>Level</th><th>Percentage (%)</th><th>Save</th></tr>
              </thead>
              <tbody>
                {sponsors.map((s, i) => (
                  <tr key={i}>
                    <td>L{i + 1}</td>
                    <td><input type="number" step="0.01" value={s} onChange={(e) => upd(i, e.target.value)} style={{ padding: '6px 8px', maxWidth: 120 }} /></td>
                    <td>
                      <NeonButton size="sm" disabled={!c.hasAdmin} loading={busy}
                        onClick={() => run('Set L' + (i + 1) + ' Sponsor', () => requireAdminWrite(c).setContributorSponsorBps(i, pctStrToBps(s)), 'Updated.')}>
                        Save
                      </NeonButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ USERS ═══════════════════════ */
const UsersTab = ({ c, run, busy }) => {
  const [id, setId] = useState('');

  const doBlacklist = (v) => {
    if (c.write.admin) return c.write.admin.setUserBlacklisted(BigInt(id || 0), v);
    return c.write.store.setUserBlacklistedAdmin(BigInt(id || 0), v);
  };

  return (
    <>
      <GlassCard title="Blacklist User">
        <div className="input-label"><span>User ID</span><span /></div>
        <input type="number" placeholder="e.g. 2001" value={id} onChange={(e) => setId(e.target.value)} />
        <div className="mt-3" style={{ display: 'flex', gap: 10 }}>
          <NeonButton variant="danger" loading={busy} onClick={() => run('Blacklist', () => doBlacklist(true), 'User blacklisted.')}>Blacklist</NeonButton>
          <NeonButton loading={busy} onClick={() => run('Unblacklist', () => doBlacklist(false), 'User unblacklisted.')}>Unblacklist</NeonButton>
        </div>
      </GlassCard>

      <div className="mt-4" />
      <AdminInvestCard c={c} run={run} busy={busy} />
    </>
  );
};

const AdminInvestCard = ({ c, run, busy }) => {
  const [wallet, setWallet] = useState('');
  const [ref, setRef] = useState('');
  const [plan, setPlan] = useState(0);
  const [amtGRX, setAmtGRX] = useState('');
  const [amtUSDT, setAmtUSDT] = useState('');
  const [contribUSDT, setContribUSDT] = useState('');
  const [invType, setInvType] = useState(1); // 1=virtual gift, 2=leader

  return (
    <GlassCard title="Admin Invest (Virtual Gift / Leader)">
      <div className="alert a-info">
        Virtual (1) is a full gift — treated as real for all income/ceiling.
        Leader (2) is tree-filler only — no ROI, no cascade, still counts for direct count / leg volume.
      </div>
      <div className="grid grid-2 mt-3">
        <div>
          <div className="input-label"><span>Wallet Address</span></div>
          <input placeholder="0x..." value={wallet} onChange={(e) => setWallet(e.target.value)} />
        </div>
        <div>
          <div className="input-label"><span>Referrer ID (0 = default)</span></div>
          <input type="number" placeholder="0" value={ref} onChange={(e) => setRef(e.target.value)} />
        </div>
        <div>
          <div className="input-label"><span>Plan</span></div>
          <select value={plan} onChange={(e) => setPlan(Number(e.target.value))}>
            {PLAN_META.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <div className="input-label"><span>Investment Type</span></div>
          <select value={invType} onChange={(e) => setInvType(Number(e.target.value))}>
            <option value={1}>Virtual (Gift)</option>
            <option value={2}>Leader (Tree-filler)</option>
          </select>
        </div>
        <div>
          <div className="input-label"><span>Amount GRX</span></div>
          <input type="number" value={amtGRX} onChange={(e) => setAmtGRX(e.target.value)} />
        </div>
        <div>
          <div className="input-label"><span>Amount USDT (declared value)</span></div>
          <input type="number" value={amtUSDT} onChange={(e) => setAmtUSDT(e.target.value)} />
        </div>
        {plan === 3 && (
          <div>
            <div className="input-label"><span>Contributor USDT (10X target base)</span></div>
            <input type="number" value={contribUSDT} onChange={(e) => setContribUSDT(e.target.value)} />
          </div>
        )}
      </div>
      <div className="mt-3">
        <NeonButton disabled={!c.hasAdmin || !isAddress(wallet)} loading={busy}
          onClick={() => run('Admin Invest',
            () => requireAdminWrite(c).adminInvest(
              wallet, BigInt(ref || 0), plan,
              parseUnits(amtGRX || '0', 18),
              parseUnits(amtUSDT || '0', 18),
              parseUnits(contribUSDT || '0', 18),
              invType
            ),
            'Admin invest created.'
          )}>
          Submit Admin Invest
        </NeonButton>
      </div>
    </GlassCard>
  );
};

/* ═══════════════════════ SYSTEM (owner only) ═══════════════════════ */
const SystemTab = ({ snap, c, run, busy, role }) => {
  return (
    <>
      <div className="grid grid-2">
        <PauseCard snap={snap} run={run} busy={busy} c={c} />
        <PayoutWalletCard snap={snap} c={c} run={run} busy={busy} />
      </div>
      <div className="mt-4" />
      <EmergencyCard snap={snap} c={c} run={run} busy={busy} />
      <div className="mt-4" />
      <RolesCard snap={snap} role={role} c={c} run={run} busy={busy} />
    </>
  );
};

const PayoutWalletCard = ({ snap, c, run, busy }) => {
  const [addr, setAddr] = useState('');
  return (
    <GlassCard title="Payout Wallet (GRX source for rewards)">
      <div className="alert a-info">
        This wallet holds GRX for reward/contributor payouts. It must approve the storage contract to spend enough GRX.
      </div>
      <div className="data-row">
        <span className="label">Current</span>
        <span className="value mono">{truncateAddress(snap?.payoutWallet)}</span>
      </div>
      <div className="data-row">
        <span className="label">Balance</span>
        <span className="value neon">{fmtGRX(snap?.payoutBal)}</span>
      </div>
      <div className="data-row">
        <span className="label">Approved to Storage</span>
        <span className="value cyan">{fmtGRX(snap?.payoutAllow)}</span>
      </div>
      <div className="input-label mt-3"><span>New Payout Wallet</span></div>
      <input placeholder="0x..." value={addr} onChange={(e) => setAddr(e.target.value)} />
      <div className="mt-2">
        <NeonButton block disabled={!isAddress(addr)} loading={busy}
          onClick={() => run('Set Payout Wallet', () => c.write.store.setPayoutWallet(addr), 'Payout wallet updated.')}>
          Update Payout Wallet
        </NeonButton>
      </div>
    </GlassCard>
  );
};

const PauseCard = ({ snap, run, busy, c }) => (
  <GlassCard title="Contract Pause">
    <div className="data-row">
      <span className="label">Current</span>
      <span className="value">{snap?.paused ? <span className="badge b-danger">PAUSED</span> : <span className="badge">LIVE</span>}</span>
    </div>
    <div className="alert a-warn mt-2">Pausing halts all logic-driven mutations. Emergency withdraw requires paused=true.</div>
    <div className="mt-3" style={{ display: 'flex', gap: 10 }}>
      <NeonButton variant="danger" disabled={snap?.paused} loading={busy} onClick={() => run('Pause', () => c.write.store.setPaused(true), 'Contract paused.')}>Pause</NeonButton>
      <NeonButton disabled={!snap?.paused} loading={busy} onClick={() => run('Unpause', () => c.write.store.setPaused(false), 'Contract resumed.')}>Unpause</NeonButton>
    </div>
  </GlassCard>
);


const EmergencyCard = ({ snap, c, run, busy }) => {
  const [token, setToken] = useState('');
  const [to, setTo] = useState('');
  const [amt, setAmt] = useState('');
  return (
    <GlassCard title="Emergency Withdraw (paused only)">
      <div className="alert a-danger">Requires contract to be PAUSED. Withdraws any BEP20 from the storage contract.</div>
      <div className="grid grid-3 mt-3">
        <div><div className="input-label"><span>Token Address</span></div><input placeholder="0x..." value={token} onChange={(e) => setToken(e.target.value)} /></div>
        <div><div className="input-label"><span>To Address</span></div><input placeholder="0x..." value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div><div className="input-label"><span>Amount (wei)</span></div><input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} /></div>
      </div>
      <div className="mt-3">
        <NeonButton variant="danger" disabled={!snap?.paused || !isAddress(token) || !isAddress(to)} loading={busy}
          onClick={() => run('Emergency Withdraw', () => c.write.store.emergencyWithdraw(token, to, BigInt(amt || 0)), 'Withdrawn.')}>Withdraw</NeonButton>
      </div>
    </GlassCard>
  );
};

const RolesCard = ({ snap, role, c, run, busy }) => {
  const [newLogic, setNewLogic] = useState('');
  const [newAdmin, setNewAdmin] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [liquidity, setLiquidity] = useState('');
  const [adminWallet, setAdminWallet] = useState('');
  const [defRef, setDefRef] = useState('');
  const [adminCt, setAdminCt] = useState('');
  const [swapLogic, setSwapLogic] = useState('');

  return (
    <GlassCard title="Roles & Wallets (Owner Only)">
      <div className="grid grid-2 mt-2">
        <div>
          <div className="input-label"><span>Authorize Logic (storage.setAuthorizedLogic)</span></div>
          <input placeholder="0x..." value={newLogic} onChange={(e) => setNewLogic(e.target.value)} />
          <div className="mt-2" style={{ display: 'flex', gap: 6 }}>
            <NeonButton size="sm" variant="cyan" disabled={!isAddress(newLogic)} loading={busy}
              onClick={() => run('Authorize', () => c.write.store.setAuthorizedLogic(newLogic, true), 'Authorized.')}>Authorize</NeonButton>
            <NeonButton size="sm" variant="danger" disabled={!isAddress(newLogic)} loading={busy}
              onClick={() => run('Revoke', () => c.write.store.setAuthorizedLogic(newLogic, false), 'Revoked.')}>Revoke</NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label"><span>Set Logic → Admin Contract</span></div>
          <input placeholder="0x..." value={adminCt} onChange={(e) => setAdminCt(e.target.value)} />
          <div className="mt-2">
            <NeonButton block disabled={!isAddress(adminCt)} loading={busy}
              onClick={() => run('Set Admin Contract', () => c.write.logic.setAdminContract(adminCt), 'Admin contract set.')}>Set Admin Contract</NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label">
            <span>Hot-swap Logic Pointer</span>
            <span>Updates PlanAdmin + PlanViews</span>
          </div>
          <input placeholder="0x... new PlanLogic" value={swapLogic} onChange={(e) => setSwapLogic(e.target.value)} />
          <div className="mono mt-1" style={{ fontSize: 11, color: 'var(--grx-text-mute)' }}>
            After redeploying PlanLogic, point PlanAdmin + PlanViews at the new address.
            Don't forget to also authorize the new logic on Storage.
          </div>
          <div className="mt-2" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <NeonButton size="sm" variant="cyan" disabled={!isAddress(swapLogic) || !c.write.admin} loading={busy}
              onClick={() => run('Set PlanAdmin.logic', () => c.write.admin.setLogic(swapLogic), 'PlanAdmin.logic updated.')}>
              PlanAdmin
            </NeonButton>
            <NeonButton size="sm" variant="cyan" disabled={!isAddress(swapLogic)} loading={busy}
              onClick={() => run('Set PlanViews.logic', () => c.write.views.setLogic(swapLogic), 'PlanViews.logic updated.')}>
              PlanViews
            </NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label"><span>Set New Admin</span></div>
          <input placeholder="0x..." value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} />
          <div className="mt-2" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <NeonButton size="sm" disabled={!isAddress(newAdmin)} loading={busy} onClick={() => run('Set Storage Admin', () => c.write.store.setAdmin(newAdmin), 'Storage admin updated.')}>Storage</NeonButton>
            <NeonButton size="sm" disabled={!isAddress(newAdmin) || !c.write.admin} loading={busy} onClick={() => run('Set PlanAdmin Admin', () => c.write.admin.setAdmin(newAdmin), 'PlanAdmin admin updated.')}>PlanAdmin</NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label"><span>Transfer Ownership</span></div>
          <input placeholder="0x..." value={newOwner} onChange={(e) => setNewOwner(e.target.value)} />
          <div className="mt-2" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <NeonButton size="sm" variant="danger" disabled={!isAddress(newOwner)} loading={busy} onClick={() => run('Transfer Logic', () => c.write.logic.transferOwnership(newOwner), 'Logic ownership transferred.')}>Logic</NeonButton>
            <NeonButton size="sm" variant="danger" disabled={!isAddress(newOwner)} loading={busy} onClick={() => run('Transfer Storage', () => c.write.store.transferOwnership(newOwner), 'Storage ownership transferred.')}>Storage</NeonButton>
            <NeonButton size="sm" variant="danger" disabled={!isAddress(newOwner) || !c.write.admin} loading={busy} onClick={() => run('Transfer PlanAdmin', () => c.write.admin.transferOwnership(newOwner), 'PlanAdmin ownership transferred.')}>PlanAdmin</NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label"><span>Set Liquidity Wallet</span></div>
          <input placeholder="0x..." value={liquidity} onChange={(e) => setLiquidity(e.target.value)} />
          <div className="mt-2">
            <NeonButton block disabled={!isAddress(liquidity)} loading={busy} onClick={() => run('Set Liquidity Wallet', () => c.write.store.setLiquidityWallet(liquidity), 'Liquidity wallet updated.')}>Update</NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label"><span>Set Admin Wallet</span></div>
          <input placeholder="0x..." value={adminWallet} onChange={(e) => setAdminWallet(e.target.value)} />
          <div className="mt-2">
            <NeonButton block disabled={!isAddress(adminWallet)} loading={busy} onClick={() => run('Set Admin Wallet', () => c.write.store.setAdminWallet(adminWallet), 'Admin wallet updated.')}>Update</NeonButton>
          </div>
        </div>
        <div>
          <div className="input-label"><span>Set Default Referrer Wallet</span></div>
          <input placeholder="0x..." value={defRef} onChange={(e) => setDefRef(e.target.value)} />
          <div className="mt-2">
            <NeonButton block disabled={!isAddress(defRef)} loading={busy} onClick={() => run('Set Default Referrer', () => c.write.store.setDefaultReferrerWallet(defRef), 'Default referrer wallet updated.')}>Update</NeonButton>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="mono uppercase text-dim" style={{ fontSize: 11, letterSpacing: '0.14em', marginBottom: 8 }}>Current Roles</div>
        <div className="data-row"><span className="label">Logic Owner</span><span className="value mono">{truncateAddress(role.logicOwner)}</span></div>
        <div className="data-row"><span className="label">Logic → AdminContract</span><span className="value mono">{truncateAddress(role.logicAdminCt)}</span></div>
        <div className="data-row"><span className="label">Storage Owner / Admin</span><span className="value mono">{truncateAddress(role.storageOwner)} / {truncateAddress(role.storageAdmin)}</span></div>
        <div className="data-row"><span className="label">PlanAdmin Owner / Admin</span><span className="value mono">{truncateAddress(role.adminOwner)} / {truncateAddress(role.adminAdmin)}</span></div>
      </div>
    </GlassCard>
  );
};

/* ═══════════════════════ NEWS ═══════════════════════ */
const NewsCard = ({ notify }) => {
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [file, setFile] = useState(null);
  const [current, setCurrent] = useState({ text: '', image: '', updatedAt: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/announcement.php', { cache: 'no-store' });
        if (r.ok) {
          const d = await r.json();
          setCurrent({ text: d.text || '', image: d.image || '', updatedAt: Number(d.updatedAt || 0) });
          setText(d.text || '');
          setImageUrl(d.image || '');
        }
      } catch {}
    })();
  }, []);

  const uploadIfNeeded = async () => {
    if (!file) return imageUrl;
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch('/api/upload.php', { method: 'POST', headers: { 'X-Admin-Key': ADMIN_API_KEY }, body: fd });
    const data = await r.json();
    if (!r.ok) throw new Error(data?.error || 'Upload failed');
    return data.path || data.url;
  };

  const publish = async () => {
    try {
      setBusy(true);
      const finalImage = await uploadIfNeeded();
      const r = await fetch('/api/announcement.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_API_KEY },
        body: JSON.stringify({ text, image: finalImage }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'Save failed');
      setCurrent({ text: data.text || '', image: data.image || '', updatedAt: Number(data.updatedAt || 0) });
      setImageUrl(data.image || '');
      setFile(null);
      notify.success('Published', 'Announcement is live.');
    } catch (e) {
      notify.error('Publish Failed', e?.message || 'Could not publish.');
    } finally { setBusy(false); }
  };

  const clearAnnouncement = async () => {
    try {
      setBusy(true);
      const r = await fetch('/api/announcement.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_API_KEY },
        body: JSON.stringify({ text: '', image: '' }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'Clear failed');
      setCurrent({ text: '', image: '', updatedAt: Number(data.updatedAt || 0) });
      setText(''); setImageUrl(''); setFile(null);
      notify.success('Cleared', 'Announcement removed.');
    } catch (e) {
      notify.error('Clear Failed', e?.message || 'Could not clear.');
    } finally { setBusy(false); }
  };

  return (
    <GlassCard title="News & Announcements">
      <div className="alert a-info">
        Marquee announcement shown at the top of every page.
      </div>

      <div className="input-label mt-3"><span>Announcement Text</span><span>{text.length}/500</span></div>
      <textarea rows={3} maxLength={500} placeholder="e.g. ROI cycle 42 has settled." value={text} onChange={(e) => setText(e.target.value)} style={{ resize: 'vertical', minHeight: 70 }} />

      <div className="input-label mt-3"><span>Image (optional)</span><span>Max 2 MB</span></div>
      <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ padding: 8 }} />
      {imageUrl && !file && (
        <div className="mt-2" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <img src={imageUrl} alt="current" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--grx-border)' }} />
          <div className="mono" style={{ fontSize: 12, color: 'var(--grx-text-dim)', wordBreak: 'break-all' }}>{imageUrl}</div>
        </div>
      )}
      {file && (
        <div className="mt-2 mono" style={{ fontSize: 12, color: 'var(--grx-cyan)' }}>
          New file: {file.name} ({(file.size / 1024).toFixed(1)} KB)
        </div>
      )}

      <div className="mt-3" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <NeonButton onClick={publish} loading={busy}>Save & Publish</NeonButton>
        <NeonButton variant="danger" onClick={clearAnnouncement} loading={busy} disabled={!current.text && !current.image}>Clear</NeonButton>
      </div>

      {current.updatedAt > 0 && (
        <div className="mono mt-3" style={{ fontSize: 11, color: 'var(--grx-text-mute)' }}>
          Last updated: {tsToDate(current.updatedAt)}
        </div>
      )}
    </GlassCard>
  );
};

/* ═══════════════════════ VIDEOS (up to 5 YouTube links) ═══════════════════════ */
const VideosCard = ({ notify }) => {
  const [rows, setRows] = useState([{ title: '', url: '' }, { title: '', url: '' }, { title: '', url: '' }, { title: '', url: '' }, { title: '', url: '' }]);
  const [busy, setBusy] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/videos.php', { cache: 'no-store' });
        if (r.ok) {
          const d = await r.json();
          const arr = Array.isArray(d.videos) ? d.videos : [];
          const next = [0,1,2,3,4].map((i) => arr[i] || { title: '', url: '' });
          setRows(next);
          setUpdatedAt(Number(d.updatedAt || 0));
        }
      } catch {}
    })();
  }, []);

  const upd = (i, k, v) => setRows((p) => p.map((r, j) => j === i ? { ...r, [k]: v } : r));

  const save = async () => {
    try {
      setBusy(true);
      const videos = rows.filter((r) => r.url && r.url.trim());
      const r = await fetch('/api/videos.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_API_KEY },
        body: JSON.stringify({ videos }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'Save failed');
      setUpdatedAt(Number(data.updatedAt || 0));
      notify.success('Saved', `${videos.length} video(s) published.`);
    } catch (e) {
      notify.error('Save Failed', e?.message || 'Could not save.');
    } finally { setBusy(false); }
  };

  return (
    <GlassCard title="YouTube Videos">
      <div className="alert a-info">
        Up to 5 videos shown as tiles at the bottom of the user's Home page. Paste any YouTube URL (watch, shorts, or youtu.be).
      </div>

      {rows.map((r, i) => (
        <div key={i} className="mt-3" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
          <input
            type="text"
            placeholder={`Title #${i + 1} (optional)`}
            value={r.title}
            onChange={(e) => upd(i, 'title', e.target.value)}
          />
          <input
            type="url"
            placeholder="https://www.youtube.com/watch?v=…"
            value={r.url}
            onChange={(e) => upd(i, 'url', e.target.value)}
          />
        </div>
      ))}

      <div className="mt-3">
        <NeonButton onClick={save} loading={busy}>Save & Publish</NeonButton>
      </div>

      {updatedAt > 0 && (
        <div className="mono mt-3" style={{ fontSize: 11, color: 'var(--grx-text-mute)' }}>
          Last updated: {tsToDate(updatedAt)}
        </div>
      )}
    </GlassCard>
  );
};

export default Admin;
